/**
 * Customized version of react-select's Creatable.
 * Similar to frontends/app/src/components/CreateRegisteredCollectionDrawer/CreatableSelect.tsx
 * Differences to handle
 *    * No dropdown options menu
 *    * Style changes wrt artifact types (and not tags/aliases)
 */

import {
  hexToRGB,
  MOON_250,
  MOON_300,
  MOON_600,
  RED_500,
  TEAL_500,
} from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {KeyboardEventHandler, useEffect, useMemo, useState} from 'react';
import {
  components,
  GroupBase,
  MultiValue,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  Props as ReactSelectProps,
  StylesConfig,
} from 'react-select';
import Creatable from 'react-select/creatable';

export const SelectSizes = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const;
export type SelectSize = (typeof SelectSizes)[keyof typeof SelectSizes];

const ICON_SIZES: Record<SelectSize, string> = {
  small: '14px',
  medium: '16px',
  large: '16px',
} as const;

const HEIGHTS: Record<SelectSize, number> = {
  small: 22,
  medium: 22,
  large: 40,
} as const;

const LINE_HEIGHTS: Record<SelectSize, string> = {
  small: '16px',
  medium: '16px',
  large: '24px',
} as const;

const FONT_SIZES: Record<SelectSize, string> = {
  small: '14px',
  medium: '14 px',
  large: '16px',
} as const;

const PADDING: Record<SelectSize, string> = {
  small: '3px 6px',
  medium: '4px 12px',
  large: '8px 12px',
} as const;

const BACKGROUND_COLOR = `${hexToRGB(MOON_300, 0.48)}`;
const HOVER_BACKGROUND_COLOR = `${hexToRGB(MOON_300, 0.64)}`;

const FONT_COLOR = `${MOON_600}`;

const MultiValueContainerWithTooltip = (
  props: MultiValueGenericProps<OptionType, true, GroupBase<OptionType>> &
    AdditionalProps
) => {
  const isRemovable = props.data.removable;
  if (!isRemovable) {
    return (
      <Tooltip
        trigger={
          <div>
            <components.MultiValueContainer {...props} />
          </div>
        }
        content="Existing artifact types cannot be removed!"
        position="bottom left"
      />
    );
  }
  return (
    <components.MultiValueLabel {...props}>
      {props.children}
    </components.MultiValueLabel>
  );
};

const MultiValueRemoveWithIcon = (
  props: MultiValueRemoveProps<OptionType, true, GroupBase<OptionType>> &
    AdditionalProps
) => {
  const size = props.size ?? SelectSizes.Small;
  return (
    <components.MultiValueRemove {...props}>
      <Icon
        name={'close'}
        style={{height: `${ICON_SIZES[size]}`, width: `${ICON_SIZES[size]}`}}
      />
      {props.children}
    </components.MultiValueRemove>
  );
};

interface OptionType {
  readonly label: string;
  readonly value: string;
  readonly removable: boolean;
}

const createOption = (label: string, removable: boolean) => ({
  label,
  value: label,
  removable,
});

interface AdditionalProps {
  size?: SelectSize;
  errorState?: boolean;
  existingArtifactTypes?: string[];
  inputArtifactTypes?: string[];
  setInputArtifactTypes?: (val: string[]) => void;
}

type Props<Option> = ReactSelectProps<Option, true> & AdditionalProps;

// See: https://react-select.com/typescript
export const ArtifactTypeMultiInput = (props: Props<OptionType>) => {
  // Override styling to come closer to design spec.
  // See: https://react-select.com/home#custom-styles
  // See: https://github.com/JedWatson/react-select/issues/2728

  const setInputArtifactTypes = props.setInputArtifactTypes;
  const inputArtifactTypes: string[] = useMemo(
    () => props.inputArtifactTypes ?? [],
    [props.inputArtifactTypes]
  );
  const errorState = props.errorState ?? false;
  const size = props.size ?? SelectSizes.Small;
  const existingArtifactTypes =
    props.existingArtifactTypes != null
      ? props.existingArtifactTypes.map((artifactType: string) =>
          createOption(artifactType, false)
        )
      : [];
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState<readonly OptionType[]>(
    existingArtifactTypes
  );

  // Update value if input existingArtifactTypes list changes
  useEffect(() => {
    const newValue: OptionType[] =
      props.existingArtifactTypes?.map((artifactType: string) =>
        createOption(artifactType, false)
      ) ?? [];

    inputArtifactTypes.forEach((artifactType: string) => {
      if (!props.existingArtifactTypes?.includes(artifactType)) {
        newValue.push(createOption(artifactType, true));
      }
    });
    setValue(newValue);
  }, [props.existingArtifactTypes, inputArtifactTypes]);

  const styles: StylesConfig<OptionType> = {
    input: baseStyles => {
      return {
        ...baseStyles,
        padding: 0,
      };
    },
    valueContainer: baseStyles => {
      const padding = PADDING[size];
      return {...baseStyles, padding};
    },
    container: baseStyles => {
      const height = HEIGHTS[size];
      return {
        ...baseStyles,
        height,
        display: 'inline',
      };
    },
    control: (baseStyles, state) => {
      const colorBorderDefault = MOON_250;
      const colorBorderHover = hexToRGB(TEAL_500, 0.4);
      const colorBorderOpen = hexToRGB(TEAL_500, 0.64);
      const height = '32px';
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      return {
        ...baseStyles,
        height: 'auto',
        minHeight: height,
        lineHeight,
        fontSize,
        border: 0,
        boxShadow: state.menuIsOpen
          ? `0 0 0 2px ${colorBorderOpen}`
          : state.isFocused
            ? `0 0 0 2px ${errorState ? RED_500 : colorBorderHover}`
            : `inset 0 0 0 1px ${colorBorderDefault}`,
        '&:hover': {
          boxShadow: state.menuIsOpen
            ? `0 0 0 2px ${colorBorderOpen}`
            : `0 0 0 2px ${colorBorderHover}`,
        },
      };
    },
    placeholder: (baseStyles, state) => {
      return {
        ...baseStyles,
        fontSize: '16px',
        lineHeight: '24px',
      };
    },
    multiValue: baseStyles => {
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      const background = BACKGROUND_COLOR;
      const hoverBackground = HOVER_BACKGROUND_COLOR;
      const fontColor = FONT_COLOR;
      return {
        ...baseStyles,
        borderRadius: '16px',
        padding: '3px 6px 3px 6px',
        height,
        lineHeight,
        fontSize,
        alignItems: 'center',
        backgroundColor: background,
        color: fontColor,
        '&:hover': {
          background: hoverBackground,
        },
      };
    },
    multiValueLabel: baseStyles => {
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      const fontColor = FONT_COLOR;
      return {
        ...baseStyles,
        height,
        lineHeight,
        fontSize,
        paddingLeft: '3',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        color: fontColor,
      };
    },
    multiValueRemove: (baseStyles, state) => {
      const fontColor = FONT_COLOR;
      return !state.data.removable
        ? {...baseStyles, display: 'none'}
        : {
            ...baseStyles,
            margin: '0',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              color: fontColor,
              background: 'none',
            },
          };
    },
  };

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!inputValue) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        if (
          !inputArtifactTypes.includes(inputValue) &&
          !props.existingArtifactTypes?.includes(inputValue)
        ) {
          setValue([...value, createOption(inputValue, true)]);
          setInputArtifactTypes?.([...inputArtifactTypes, inputValue]);
        }
        setInputValue('');
        event.preventDefault();
    }
  };

  return (
    <Creatable
      components={{
        DropdownIndicator: null,
        MultiValueRemove: MultiValueRemoveWithIcon,
        MultiValueContainer: MultiValueContainerWithTooltip,
      }}
      noOptionsMessage={() => null}
      inputValue={inputValue}
      isClearable={false}
      isMulti={true}
      styles={styles}
      defaultMenuIsOpen={false}
      onKeyDown={handleKeyDown}
      value={value}
      onChange={(newValue: MultiValue<OptionType>) => {
        if (newValue != null) {
          setValue(newValue);
          const newInputArtifactTypes: string[] = [];
          for (const val of newValue) {
            if (!props.existingArtifactTypes?.includes(val.value)) {
              newInputArtifactTypes.push(val.value);
            }
          }
          setInputArtifactTypes?.(newInputArtifactTypes);
        }
      }}
      onInputChange={newValue => setInputValue(newValue)}
      placeholder="Add types..."
      {...props}
    />
  );
};
